import {
	GSM_7_BASIC,
	GSM_7_EXTENDED,
	MAX_ALLOWED_MESSAGE_LENGTH,
	MAX_ALLOWED_MESSAGE_SEGMENTS,
} from 'constants/validations';

const GSM_7_BASIC_SET = new Set(GSM_7_BASIC);
const GSM_7_EXTENDED_SET = new Set(GSM_7_EXTENDED);

export const GSM_SINGLE_SEGMENT_LIMIT = 160;
export const GSM_MULTI_SEGMENT_LIMIT = 153;
export const UCS2_SINGLE_SEGMENT_LIMIT = 70;
export const UCS2_MULTI_SEGMENT_LIMIT = 67;

// Friendly names for characters that are invisible or easy to confuse with
// their GSM-7 lookalike, so UI copy can name them instead of printing blank
// space or a character that looks identical to the one it's replacing.
const CHAR_NAMES = {
	' ': 'non-breaking space',
	' ': 'narrow non-breaking space',
	' ': 'thin space',
	' ': 'figure space',
	'​': 'zero-width space',
	'‘': 'left single quote',
	'’': 'right single quote',
	'‚': 'single low quote',
	'‛': 'single high-reversed quote',
	'“': 'left double quote',
	'”': 'right double quote',
	'„': 'double low quote',
	'‟': 'double high-reversed quote',
	'–': 'en dash',
	'—': 'em dash',
	'−': 'minus sign',
	'…': 'ellipsis',
};

export function describeChar(char) {
	return CHAR_NAMES[char] || char;
}

export function isGsm7Char(char) {
	return GSM_7_BASIC_SET.has(char) || GSM_7_EXTENDED_SET.has(char);
}

// The max character count that keeps a message within
// MAX_ALLOWED_MESSAGE_SEGMENTS segments, for a given encoding. Uses the
// multi-segment per-segment rate (not the single-segment one) since a
// message long enough to approach the segment cap is, by definition, a
// multi-segment message. Capped at MAX_ALLOWED_MESSAGE_LENGTH, the backend
// limit, so GSM-7 ends up at 1000 (not 1530) and UCS-2 at 670.
export function getMaxMessageLength(encoding) {
	const perSegment =
		encoding === 'UCS-2' ? UCS2_MULTI_SEGMENT_LIMIT : GSM_MULTI_SEGMENT_LIMIT;
	return Math.min(
		perSegment * MAX_ALLOWED_MESSAGE_SEGMENTS,
		MAX_ALLOWED_MESSAGE_LENGTH,
	);
}

export function analyzeSms(message) {
	const nonGsmChars = [];
	let gsmLength = 0;
	let index = 0;

	for (const char of message) {
		if (GSM_7_BASIC_SET.has(char)) {
			gsmLength += 1;
		} else if (GSM_7_EXTENDED_SET.has(char)) {
			gsmLength += 2;
		} else {
			nonGsmChars.push({ char, index });
		}
		index += char.length;
	}

	if (nonGsmChars.length > 0) {
		const totalLength = message.length;
		const segments =
			totalLength <= UCS2_SINGLE_SEGMENT_LIMIT
				? 1
				: Math.ceil(totalLength / UCS2_MULTI_SEGMENT_LIMIT);

		return {
			encoding: 'UCS-2',
			segments,
			perSegment:
				segments === 1 ? UCS2_SINGLE_SEGMENT_LIMIT : UCS2_MULTI_SEGMENT_LIMIT,
			totalLength,
			nonGsmChars,
		};
	}

	const segments =
		gsmLength <= GSM_SINGLE_SEGMENT_LIMIT
			? 1
			: Math.ceil(gsmLength / GSM_MULTI_SEGMENT_LIMIT);

	return {
		encoding: 'GSM-7',
		segments,
		perSegment:
			segments === 1 ? GSM_SINGLE_SEGMENT_LIMIT : GSM_MULTI_SEGMENT_LIMIT,
		totalLength: gsmLength,
		nonGsmChars: [],
	};
}
